import { Flex, Table, Tag, Input, Typography,Avatar } from "antd";
import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { colors } from "../../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { SearchOutlined,ShopOutlined } from "@ant-design/icons";
import { getServiceProvidersAction } from "../../../../../../redux/services/service-providers/serviceProvidersServices";
import AcceptRejectStatusDialog from "./AcceptRejectStatusDialog";
import Actions from "../../../../../../redux/actions";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import { routes } from "../../../../../../constants";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";

const ServiceProviderAuth = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const { serviceProviders } = useSelector((state) => state.serviceProviders);
  const [columns, setColumns] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviders?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openAcceptRejectStatusDialog, setOpenAcceptRejectStatusDialog] =
    useState(false);
  const [status, setStatus] = useState("");
  /* dialog dependencies */

  /* debounced loading for search */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    1000
  );
  /* debounced loading for search */

  /* function definition to get table data */
  const handleGetTableData = () => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };

    dispatch(getServiceProvidersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Appointments.serviceProvider"),
        dataIndex: "name",
        key: "name",
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                size="large"
                src={record?.logo_image}
                key={record?.logo_image}
                icon={<ShopOutlined />}
              />
               {
                record?.name
            ? `${record?.name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("Modules.Notify.city"),
        dataIndex: "address",
        key: "address",
        align: "center",
        render: (address) => `${address?.city?.name || "-"}`,
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "organization_verified",
        dataIndex: "organization_verified",
        align: "center",
        render: (_, { organization_verified }) => (
          <Tag
            color={organization_verified ? "green" : "red"}
            key={organization_verified}
          >
            {organization_verified ? t("CommonUtils.verified") : t("CommonUtils.notVerified")}
          </Tag>
        ),
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {!record?.organization_verified ? (
                <Flex gap="small" justify="center">
                  <Tag
                    bordered={false}
                    style={{
                      background: colors.lightTeal,
                      color: colors.darkTeal,
                      cursor: "pointer",
                    }}
                    icon={<CheckCircleFilled />}
                    onClick={(e) =>
                      handleConfirmStatus(e, record, "isAccepted")
                    }
                  >
                    {t("CommonUtils.accept")}
                  </Tag>

                  <Tag
                    bordered={false}
                    style={{
                      background: colors.lightPinkishRed,
                      color: colors.darkMaroon,
                      cursor: "pointer",
                    }}
                    icon={<CloseCircleFilled />}
                    onClick={(e) =>
                      handleConfirmStatus(e, record, "isRejected")
                    }
                  >
                    {t("CommonUtils.reject")}
                  </Tag>
                </Flex>
              ) : (
                <>-</>
              )}
            </>
          );
        },
      },
      {
        title: t("Modules.ServiceProviderAuth.verificationRemarks"),
        dataIndex: "verification_remark",
        key: "verification_remark",

        render: (verification_remark) => (
          <div
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "150px",
              overflow: "hidden",
            }}
          >
            {verification_remark || "-"}
          </div>
        ),
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        align: "center",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.ServiceProviderAuth.details"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* initial dependencies rendering */

  const handleConfirmStatus = (e, row, status) => {
    setStatus(status);
    setOpenAcceptRejectStatusDialog(true);
    // dispatch(
    //   Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
    //     selectedServiceProviderId: "",
    //   })
    // );
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceProviderId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
          selectedServiceProviderId,
        })
      );
    }
  };

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
        selectedServiceProviderId: "",
        navigateBack: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.SERVICE_PROVIDER_AUTH_DETAILS));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceProviderId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
          selectedServiceProviderId,
          navigateBack: baseRouteMakerForSuperAdmin(
            routes.SERVICE_PROVIDER_AUTH
          ),
        })
      );
    }
  };

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Title level={2}>
            {t("Modules.ServiceProviderAuth.serviceProviderAuth")}
          </Title>
          <Input
            size="large"
            suffix={<SearchOutlined />}
            placeholder={t("CommonUtils.searchByName")}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
            style={{ width: "370px" }}
          />
        </Flex>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviders?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
      <AcceptRejectStatusDialog
        status={status}
        open={openAcceptRejectStatusDialog}
        onClose={() => {
          setOpenAcceptRejectStatusDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default ServiceProviderAuth;
